// Customer data export — CSV / XLSX / PDF builders.
//
// Shares the same architecture as the Reports export: sync CSV
// (no deps), async XLSX + PDF (lazy-loaded libs). The PDF gets the
// same branded treatment as the Reports PDF: company header bar,
// zebra-striped table, IDR formatting where relevant.

import type { Customer } from '@/types/domain';
import { loadJsPdf } from '@/lib/lazyChunk';
import { loadXlsx } from '@/lib/spreadsheet';

export interface CustomerExportFilters {
  /** Status filter — "all" exports active + inactive; default active. */
  status?: 'all' | 'active' | 'inactive';
  /** Optional search term to filter by name/company/phone/email. */
  search?: string;
}

export interface CustomerExportCompanyInfo {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  logoUrl?: string;
}

/** Normalize a customer into the row shape we use for all 3 formats. */
function toRow(c: Customer) {
  return {
    name: c.name,
    company: c.company ?? '',
    phone: c.phone,
    email: c.email ?? '',
    status: c.isActive ? 'Active' : 'Inactive',
    createdAt: c.createdAt,
  };
}

function filterCustomers(
  customers: Customer[],
  filters: CustomerExportFilters = {},
): Customer[] {
  const { status = 'all', search = '' } = filters;
  const term = search.trim().toLowerCase();
  return customers.filter((c) => {
    if (status === 'active' && !c.isActive) return false;
    if (status === 'inactive' && c.isActive) return false;
    if (term) {
      const hay = [
        c.name,
        c.company ?? '',
        c.phone,
        c.email ?? '',
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  });
}

// ── CSV ────────────────────────────────────────────────────────────
function escapeCsv(v: string | number): string {
  const s = String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildCustomerCsv(
  customers: Customer[],
  filters?: CustomerExportFilters,
): string {
  const rows = filterCustomers(customers, filters).map(toRow);
  const lines: string[] = [];
  lines.push('OPHTH POS — Customer Export');
  lines.push(`Generated,${new Date().toISOString()}`);
  lines.push(`Filter,status=${filters?.status ?? 'all'}${filters?.search ? `,search="${filters.search}"` : ''}`);
  lines.push(`Count,${rows.length}`);
  lines.push('');
  lines.push('Name,Company,Phone,Email,Status,Created At');
  for (const r of rows) {
    lines.push(
      [
        escapeCsv(r.name),
        escapeCsv(r.company),
        escapeCsv(r.phone),
        escapeCsv(r.email),
        escapeCsv(r.status),
        new Date(r.createdAt).toISOString(),
      ].join(','),
    );
  }
  return lines.join('\n');
}

// ── XLSX ───────────────────────────────────────────────────────────
export async function buildCustomerXlsx(
  customers: Customer[],
  filters?: CustomerExportFilters,
): Promise<Blob> {
  const XLSX = await loadXlsx();
  const rows = filterCustomers(customers, filters).map(toRow);

  const aoa: (string | number)[][] = [
    ['Name', 'Company', 'Phone', 'Email', 'Status', 'Created At'],
    ...rows.map((r) => [
      r.name,
      r.company,
      r.phone,
      r.email,
      r.status,
      new Date(r.createdAt).toLocaleString('id-ID'),
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Column widths based on content length
  const colWidths = aoa[0].map((_, i) => {
    const maxLen = aoa.reduce((m, row) => {
      const s = String(row[i] ?? '');
      return Math.max(m, s.length);
    }, 0);
    return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
  });
  ws['!cols'] = colWidths;
  // Freeze header
  if (aoa.length > 1) ws['!freeze'] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Customers');

  const arrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// ── PDF ────────────────────────────────────────────────────────────
export async function buildCustomerPdf(
  customers: Customer[],
  filters: CustomerExportFilters = {},
  company: CustomerExportCompanyInfo = { name: 'OPHTH POS' },
): Promise<Blob> {
  const { jsPDF } = await loadJsPdf();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  // ── Colors (RGB) ────────────────────────────────────────────────
  const C = {
    primary: [14, 116, 144] as [number, number, number],
    accent: [217, 119, 6] as [number, number, number],
    success: [22, 163, 74] as [number, number, number],
    danger: [220, 38, 38] as [number, number, number],
    ink: [17, 24, 39] as [number, number, number],
    muted: [107, 114, 128] as [number, number, number],
    border: [226, 232, 240] as [number, number, number],
    cardBg: [248, 250, 252] as [number, number, number],
    band: [241, 245, 249] as [number, number, number],
    rowAlt: [252, 252, 253] as [number, number, number],
  };

  // ── Layout ──────────────────────────────────────────────────────
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const M = 40;
  const CONTENT_W = PAGE_W - M * 2;
  const FOOTER_H = 30;
  const HEADER_H = 44;
  let y = M;

  const setRgb = (c: [number, number, number]) => {
    doc.setTextColor(c[0], c[1], c[2]);
    doc.setDrawColor(c[0], c[1], c[2]);
  };
  const fit = (s: string, max: number) =>
    s.length > max ? s.slice(0, max - 1) + '…' : s;
  const fmtNum = (n: number) => n.toLocaleString('id-ID');
  const fmtDate = (ms: number) =>
    new Date(ms).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const filtered = filterCustomers(customers, filters);
  const rows = filtered.map(toRow);

  // ── Header (re-drawn on every page) ─────────────────────────────
  const drawHeaderBar = () => {
    doc.setFillColor(...C.primary);
    doc.rect(0, 0, PAGE_W, 6, 'F');
    y = M;
    doc.setFillColor(...C.cardBg);
    doc.rect(M, y - 4, CONTENT_W, HEADER_H - 6, 'F');

    const LOGO_SIZE = 32;
    const LOGO_X = M + 6;
    const LOGO_Y = y - 0;
    doc.setFillColor(...C.primary);
    doc.roundedRect(LOGO_X, LOGO_Y, LOGO_SIZE, LOGO_SIZE, 5, 5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text(
      (company.name || 'O').charAt(0).toUpperCase(),
      LOGO_X + LOGO_SIZE / 2,
      LOGO_Y + LOGO_SIZE / 2 + 5.5,
      { align: 'center' },
    );

    const textX = LOGO_X + LOGO_SIZE + 12;
    setRgb(C.ink);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(fit(company.name, 40), textX, y + 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setRgb(C.muted);
    const contactParts = [company.address, company.phone, company.email]
      .filter(Boolean) as string[];
    if (contactParts.length > 0) {
      doc.text(fit(contactParts.join(' · '), 90), textX, y + 28);
    }

    y = M + HEADER_H;
  };

  const ensureSpace = (h: number) => {
    if (y + h > PAGE_H - M - FOOTER_H) {
      doc.addPage();
      y = M;
      drawHeaderBar();
    }
  };

  // ── Footer (drawn on every page after body is complete) ─────────
  const drawFooters = () => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      const fy = PAGE_H - M + 10;
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.5);
      doc.line(M, fy - 14, M + CONTENT_W, fy - 14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      setRgb(C.muted);
      const leftText = fit(
        company.name + (company.address ? ' · ' + company.address.split('\n')[0] : ''),
        80,
      );
      doc.text(leftText, M, fy);
      doc.text(`Halaman ${i} dari ${pageCount}`, M + CONTENT_W, fy, {
        align: 'right',
      });
    }
  };

  // ── Section title ───────────────────────────────────────────────
  const sectionTitle = (text: string, sub?: string) => {
    ensureSpace(40);
    y += 4;
    doc.setFillColor(...C.accent);
    doc.rect(M, y - 8, 4, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    setRgb(C.ink);
    doc.text(text, M + 12, y + 4);
    if (sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setRgb(C.muted);
      doc.text(sub, M + 12, y + 18);
      y += 10;
    }
    y += 18;
  };

  // ── Styled table ────────────────────────────────────────────────
  type Column = {
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    width: number;
    format?: (v: unknown) => string;
  };

  const drawStyledTable = (columns: Column[], data: Array<Record<string, unknown>>) => {
    if (data.length === 0) {
      ensureSpace(40);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      setRgb(C.muted);
      doc.text('(tidak ada customer yang cocok dengan filter)', M, y + 8);
      y += 20;
      return;
    }

    const colX = columns.reduce<{ x: number[]; w: number[] }>(
      (acc, col) => {
        const w = CONTENT_W * col.width;
        acc.x.push(acc.x.length === 0 ? M : acc.x[acc.x.length - 1] + acc.w[acc.w.length - 1]);
        acc.w.push(w);
        return acc;
      },
      { x: [], w: [] },
    );
    const ROW_H = 24;
    const HEADER_H = 26;

    // Header
    ensureSpace(HEADER_H + ROW_H);
    doc.setFillColor(...C.band);
    doc.rect(M, y - 4, CONTENT_W, HEADER_H, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setRgb(C.ink);
    columns.forEach((col, i) => {
      const tx = colX.x[i];
      const tw = colX.w[i];
      const align = col.align ?? 'left';
      const padX = 8;
      const textX =
        align === 'right'
          ? tx + tw - padX
          : align === 'center'
            ? tx + tw / 2
            : tx + padX;
      doc.text(col.label.toUpperCase(), textX, y + 11, { align });
    });
    y += HEADER_H;

    // Body
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    data.forEach((row, idx) => {
      ensureSpace(ROW_H);
      if (idx % 2 === 0) {
        doc.setFillColor(...C.rowAlt);
        doc.rect(M, y - 4, CONTENT_W, ROW_H, 'F');
      }
      setRgb(C.ink);
      columns.forEach((col, i) => {
        const tx = colX.x[i];
        const tw = colX.w[i];
        const align = col.align ?? 'left';
        const padX = 8;
        const textX =
          align === 'right'
            ? tx + tw - padX
            : align === 'center'
              ? tx + tw / 2
              : tx + padX;
        const raw = row[col.key];
        const text =
          col.format && raw !== undefined && raw !== null
            ? col.format(raw)
            : raw === undefined || raw === null
              ? '—'
              : String(raw);
        const maxChars = Math.max(6, Math.floor(tw / 5));
        const finalText =
          align === 'left' && text.length > maxChars
            ? text.slice(0, maxChars - 1) + '…'
            : text;
        doc.text(finalText, textX, y + 12, { align });
      });
      y += ROW_H;
    });
    y += 8;
  };

  // ════════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════════
  drawHeaderBar();
  y += 6;

  // Cover title
  ensureSpace(80);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  setRgb(C.primary);
  doc.text('Daftar Customer', M, y + 22);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  setRgb(C.muted);
  doc.text('Customer master data export', M, y + 40);
  y += 54;

  // Filter summary card
  ensureSpace(60);
  doc.setFillColor(...C.cardBg);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.5);
  doc.roundedRect(M, y, CONTENT_W, 56, 4, 4, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  setRgb(C.ink);
  doc.text(`${fmtNum(filtered.length)} customer`, M + 12, y + 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setRgb(C.muted);
  const filterDesc = [
    `Status: ${filters.status ?? 'all'}`,
    filters.search ? `Pencarian: "${filters.search}"` : null,
    `Generated: ${new Date().toLocaleString('id-ID')}`,
  ]
    .filter(Boolean)
    .join('  ·  ');
  doc.text(fit(filterDesc, 100), M + 12, y + 38);
  y += 70;

  // ── Customer table ──
  sectionTitle(
    'Daftar Customer',
    `${fmtNum(rows.length)} baris · diurut dari nama A→Z`,
  );

  // Lay out columns with widths that sum to 1.0
  const cols: Column[] = [
    { key: 'name', label: 'Nama', width: 0.22 },
    { key: 'company', label: 'Perusahaan', width: 0.2 },
    { key: 'phone', label: 'Telepon', width: 0.15 },
    { key: 'email', label: 'Email', width: 0.22 },
    { key: 'status', label: 'Status', align: 'center', width: 0.1 },
    { key: 'createdAt', label: 'Terdaftar', align: 'right', width: 0.11, format: (v) => fmtDate(Number(v)) },
  ];

  // Sort alphabetically by name (Indonesian locale)
  const sorted = [...rows].sort((a, b) =>
    a.name.localeCompare(b.name, 'id'),
  );

  drawStyledTable(cols, sorted);

  // After the table, we want to overlay status pills on the Status
  // column for each row. jspdf doesn't support per-cell rendering
  // easily, so we re-draw the status pills in a second pass. The
  // positioning math is fragile; instead, we use a simpler approach:
  // colour the Status text cell directly.
  // (Implementation note: we leave the text as-is. The 'Active'/
  // 'Inactive' text is the visual indicator. A future improvement
  // could replace text with a coloured rect — see TODO below.)

  drawFooters();
  return doc.output('blob');
}
